import { innerPageCount, resolveExport, type AlbumDocument, type Background, type ExportOptions } from '@freealbum/document';
import type { ViewerBook } from './dto';

/** The schema's own cover colour: what a photo or pattern cover falls back to where only a colour can be painted. */
export const COVER_FALLBACK_COLOR = '#1b1b1f';

const rgb = (color: string): number[] | null => {
  const m = /^#([0-9a-f]{6})$/i.exec(color.trim());
  return m ? [0, 2, 4].map((i) => parseInt(m[1]!.slice(i, i + 2), 16)) : null;
};

/**
 * The one colour that best stands in for a background where the viewer can only paint a colour:
 * a gradient's midpoint (which is what lands on the spine), a pattern's tint, a photo's fallback.
 */
export function surfaceColor(background: Background | null | undefined, fallback = COVER_FALLBACK_COLOR): string {
  if (!background) return fallback;
  if (background.type === 'color') return background.color;
  if (background.type === 'gradient') {
    const from = rgb(background.from);
    const to = rgb(background.to);
    if (!from || !to) return background.from;
    return `#${from.map((v, i) => Math.round((v + to[i]!) / 2).toString(16).padStart(2, '0')).join('')}`;
  }
  if (background.type === 'asset') return background.tint ?? fallback;
  return fallback;
}

/** Geometry of the saved album; motion coefficients in the UI are an approximation of paper. */
export function viewerBook(doc: AlbumDocument, options?: ExportOptions): ViewerBook {
  const resolved = options ? resolveExport(doc, options) : null;
  const coverColor = surfaceColor(doc.cover.background);
  return {
    widthMm: doc.page.widthMm,
    heightMm: doc.page.heightMm,
    spineMm: resolved?.coverSpineMm ?? doc.cover.spineWidthMm,
    pageCount: innerPageCount(doc),
    binding: doc.print.binding,
    direction: doc.cover.binding,
    coverColor,
    // A spine of its own colour is the common case; otherwise the cover wraps straight across it.
    spineColor: surfaceColor(doc.cover.spineBackground, coverColor),
    hasCover: doc.cover.enabled && (!resolved || resolved.cover !== 'none'),
  };
}
